import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "panel"]
  static values = {
    submitOnChange: { type: Boolean, default: false },
    modal: { type: Boolean, default: false },
  }

  connect() {
    this.closeWithEscapeBound = this.closeWithEscape.bind(this)
    this.submitOnChangeBound = this.submitOnChange.bind(this)
    this.submitToggleClickBound = this.submitToggleClick.bind(this)

    document.addEventListener("keydown", this.closeWithEscapeBound)
    this.element.addEventListener("change", this.submitOnChangeBound)
    this.element.addEventListener("click", this.submitToggleClickBound)
    this.syncTrigger()
  }

  disconnect() {
    document.removeEventListener("keydown", this.closeWithEscapeBound)
    this.element.removeEventListener("change", this.submitOnChangeBound)
    this.element.removeEventListener("click", this.submitToggleClickBound)
  }

  toggle(event) {
    event?.preventDefault()
    this.isOpen() ? this.close(event) : this.open(event)
  }

  open(event) {
    event?.preventDefault()

    this.panelTarget.classList.remove("hidden")
    this.syncTrigger()
    this.focusPanel()
  }

  close(event) {
    event?.preventDefault()

    if (!this.hasPanelTarget) {
      return
    }

    this.panelTarget.classList.add("hidden")
    this.syncTrigger()

    if (this.hasTriggerTarget) {
      this.triggerTarget.focus()
    }
  }

  clearAll(event) {
    event?.preventDefault()
    this.clearingAll = true

    this.dropdownElements().forEach((element) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        "railsui-dropdown-menu"
      )
      const defaultValue = element.dataset.defaultValue

      if (defaultValue && controller?.selectValue) {
        controller.selectValue(defaultValue)
        return
      }

      if (controller?.clearSelections) {
        controller.clearSelections()
        return
      }

      element.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
        checkbox.checked = false
        checkbox.indeterminate = false
        checkbox.dispatchEvent(new Event("change", { bubbles: true }))
      })
    })

    this.clearNativeControls()
    this.clearingAll = false
    this.syncActiveFromFilters()
    this.submitForm()
  }

  closeWithEscape(event) {
    if (event.key === "Tab") {
      this.trapFocus(event)
      return
    }

    if (event.key !== "Escape" || !this.isOpen()) {
      return
    }

    this.close(event)
  }

  trapFocus(event) {
    if (!this.modalValue || !this.element.isConnected || !this.isOpen()) {
      return
    }

    const focusable = Array.from(
      this.panelTarget.querySelectorAll("button:not([disabled]), [href], input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])")
    ).filter((element) => !element.closest(".hidden, [hidden]"))

    if (focusable.length === 0) {
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const isFocusOutsidePanel = !this.panelTarget.contains(document.activeElement)

    if (event.shiftKey && (document.activeElement === first || isFocusOutsidePanel)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (document.activeElement === last || isFocusOutsidePanel)) {
      event.preventDefault()
      first.focus()
    }
  }

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")
  }

  syncTrigger() {
    if (!this.hasTriggerTarget) {
      return
    }

    this.triggerTarget.setAttribute("aria-expanded", this.isOpen().toString())
    this.syncActiveFromFilters()
  }

  syncActiveFromFilters() {
    if (!this.hasTriggerTarget) {
      return
    }

    const active = this.hasActiveFilters()
    this.triggerTarget.classList.toggle("is-active", active)
    this.triggerTarget.setAttribute("aria-pressed", active.toString())
  }

  hasActiveFilters() {
    if (this.dropdownElements().some((element) => this.dropdownHasSelection(element))) {
      return true
    }

    if (!this.hasPanelTarget) {
      return false
    }

    return Array.from(this.panelTarget.querySelectorAll("input, select, textarea")).some((control) => {
      if (this.isDropdownControl(control) || control.type === "hidden" || control.disabled) {
        return false
      }

      if (["checkbox", "radio"].includes(control.type)) {
        return control.checked
      }

      if (control.tagName === "SELECT") {
        return control.multiple
          ? Array.from(control.selectedOptions).some((option) => option.value)
          : Boolean(control.value)
      }

      return Boolean(control.value)
    })
  }

  dropdownHasSelection(element) {
    const controller = this.application.getControllerForElementAndIdentifier(
      element,
      "railsui-dropdown-menu"
    )

    if (controller?.hasHiddenInputTarget) {
      const value = controller.hiddenInputTarget.value
      return Boolean(value) && value !== element.dataset.defaultValue
    }

    return Array.from(element.querySelectorAll("input[type='checkbox']")).some((checkbox) => checkbox.checked)
  }

  focusPanel() {
    if (!this.hasPanelTarget) {
      return
    }

    this.panelTarget.focus()
  }

  dropdownElements() {
    if (!this.hasPanelTarget) {
      return []
    }

    return Array.from(this.panelTarget.querySelectorAll("[data-controller~='railsui-dropdown-menu']"))
  }

  clearNativeControls() {
    if (!this.hasPanelTarget) {
      return
    }

    this.panelTarget
      .querySelectorAll("input, select, textarea")
      .forEach((control) => {
        if (this.isDropdownControl(control) || control.type === "hidden" || control.disabled) {
          return
        }

        if (["checkbox", "radio"].includes(control.type)) {
          control.checked = false
          control.indeterminate = false
        } else if (control.tagName === "SELECT") {
          control.selectedIndex = control.multiple ? -1 : 0
        } else {
          control.value = ""
        }

        control.dispatchEvent(new Event("change", { bubbles: true }))
      })

    this.panelTarget
      .querySelectorAll("button[aria-pressed='true'], [role='switch'][aria-checked='true']")
      .forEach((toggle) => {
        if (this.isPanelChrome(toggle) || this.isDropdownControl(toggle) || toggle.disabled) {
          return
        }

        toggle.setAttribute("aria-pressed", "false")
        toggle.setAttribute("aria-checked", "false")
      })
  }

  submitOnChange(event) {
    if (!this.hasPanelTarget || !this.panelTarget.contains(event.target)) {
      return
    }

    this.syncActiveFromFilters()

    if (this.clearingAll || !this.submitOnChangeValue) {
      return
    }

    this.submitForm()
  }

  submitToggleClick(event) {
    if (!this.submitOnChangeValue || !this.hasPanelTarget || !this.panelTarget.contains(event.target)) {
      return
    }

    const toggle = event.target.closest("button[aria-pressed], [role='switch'], [data-filter-panel-submit]")

    if (!toggle || this.isPanelChrome(toggle) || this.isDropdownControl(toggle)) {
      return
    }

    window.requestAnimationFrame(() => this.submitForm())
  }

  submitForm() {
    if (!this.submitOnChangeValue) {
      return
    }

    const form = this.element.matches("form") ? this.element : this.element.closest("form")

    if (!form) {
      return
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
      return
    }

    form.submit()
  }

  isDropdownControl(element) {
    return Boolean(element.closest("[data-controller~='railsui-dropdown-menu']"))
  }

  isPanelChrome(element) {
    return Boolean(element.closest(".filter-panel-clear, .filter-panel-close"))
  }
}
